import { t } from "@lingui/core/macro";
import * as Option from "effect/Option";
import * as Arr from "effect/Array";
import type {
  ClientOrchestrationThreadActivity,
  OrchestrationLatestTurn,
  TurnId,
} from "@t3tools/contracts";
import {
  compareActivitiesByOrder,
  toPayloadRecord,
  type WorkLogEntry,
} from "@t3tools/shared/workLog";
import {
  isLatestTurnSettled,
  type LatestTurnTiming,
  type SessionActivityState,
  type TimelineEntry as SharedTimelineEntry,
} from "@t3tools/shared/messageTimeline";
export {
  deriveWorkLogEntries,
  deriveWorkLogRawFields,
  isServiceActivity,
  type WorkLogEntry,
  type WorkLogRawFields,
} from "@t3tools/shared/workLog";
export { deriveTimelineEntries, isLatestTurnSettled } from "@t3tools/shared/messageTimeline";

import type { ChatMessage, SessionPhase, ThreadSession } from "./types";

export interface ActivePlanState {
  createdAt: string;
  turnId: TurnId | null;
  explanation?: string | null;
  steps: Array<{
    step: string;
    status: "pending" | "inProgress" | "completed";
  }>;
}

export type TimelineEntry = SharedTimelineEntry<ChatMessage, WorkLogEntry>;

export function formatDuration(durationMs: number): string {
  if (!Number.isFinite(durationMs) || durationMs < 0) {
    const milliseconds = 0;
    return t`${milliseconds}ms`;
  }
  if (durationMs < 1_000) {
    const milliseconds = Math.max(1, Math.round(durationMs));
    return t`${milliseconds}ms`;
  }
  if (durationMs < 60_000) {
    const seconds =
      durationMs < 10_000 ? (durationMs / 1_000).toFixed(1) : Math.round(durationMs / 1_000);
    return t`${seconds}s`;
  }
  let minutes = Math.floor(durationMs / 60_000);
  let seconds = Math.round((durationMs % 60_000) / 1_000);
  if (seconds === 60) {
    minutes += 1;
    seconds = 0;
  }
  if (seconds === 0) return t`${minutes}m`;
  return `${t`${minutes}m`} ${t`${seconds}s`}`;
}

/** Whole seconds for a ticking clock: `17s`, `1m 05s`. */
export function formatClock(elapsedMs: number): string {
  const total = Number.isFinite(elapsedMs) && elapsedMs > 0 ? Math.floor(elapsedMs / 1_000) : 0;
  const minutes = Math.floor(total / 60);
  if (minutes === 0) {
    const seconds = total;
    return t`${seconds}s`;
  }
  const seconds = String(total % 60).padStart(2, "0");
  return `${t`${minutes}m`} ${t`${seconds}s`}`;
}

export function formatElapsed(startIso: string, endIso: string | undefined): string | null {
  if (!endIso) return null;
  const startedAt = Date.parse(startIso);
  const endedAt = Date.parse(endIso);
  if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt < startedAt) {
    return null;
  }
  return formatDuration(endedAt - startedAt);
}

export function deriveActiveWorkStartedAt(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
  sendStartedAt: string | null,
): string | null {
  const runningTurnId =
    session?.orchestrationStatus === "running" ? (session.activeTurnId ?? null) : null;
  if (runningTurnId !== null) {
    if (latestTurn?.turnId === runningTurnId) {
      return latestTurn.startedAt ?? sendStartedAt;
    }
    return sendStartedAt;
  }
  if (!isLatestTurnSettled(latestTurn, session)) {
    return latestTurn?.startedAt ?? sendStartedAt;
  }
  return sendStartedAt;
}

export function deriveActivePlanState(
  activities: ReadonlyArray<ClientOrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): ActivePlanState | null {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const allPlanActivities = ordered.filter((activity) => activity.kind === "turn.plan.updated");
  // Prefer plan from the current turn; fall back to the most recent plan from any turn
  // so that TodoWrite tasks persist across follow-up messages.
  const latest = Option.firstSomeOf([
    ...(latestTurnId
      ? Arr.findLast(allPlanActivities, (activity) => activity.turnId === latestTurnId)
      : Option.none()),
    Arr.last(allPlanActivities),
  ]).pipe(Option.getOrNull);
  if (!latest) {
    return null;
  }
  const payload = toPayloadRecord(latest.payload);
  const rawPlan = payload?.plan;
  if (!Array.isArray(rawPlan)) {
    return null;
  }
  const steps: Array<{
    step: string;
    status: "pending" | "inProgress" | "completed";
  }> = [];
  for (const entry of rawPlan) {
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const record = entry as Record<string, unknown>;
    if (typeof record.step !== "string") {
      continue;
    }
    const status =
      record.status === "completed" || record.status === "inProgress" ? record.status : "pending";
    steps.push({
      step: record.step,
      status,
    });
  }
  if (steps.length === 0) {
    return null;
  }
  return {
    createdAt: latest.createdAt,
    turnId: latest.turnId,
    ...(payload && "explanation" in payload
      ? { explanation: payload.explanation as string | null }
      : {}),
    steps,
  };
}

/**
 * Prompt each sub-agent was started with, keyed by its child thread id. The
 * prompt only ever travels on the parent's task activities, so a sub-agent
 * surface that renders children without their parent's work log has to read it
 * from here.
 */
export function buildSubAgentTaskPromptByChildThreadId(
  activities: ReadonlyArray<ClientOrchestrationThreadActivity>,
): Map<string, string> {
  const promptByChildThreadId = new Map<string, string>();
  for (const activity of activities) {
    const payload = toPayloadRecord(activity.payload);
    if (!payload) continue;
    const childThreadId = payload.childThreadId;
    const prompt = payload.prompt;
    if (typeof childThreadId !== "string" || childThreadId.length === 0) continue;
    if (typeof prompt !== "string" || prompt.trim().length === 0) continue;
    if (!promptByChildThreadId.has(childThreadId)) {
      promptByChildThreadId.set(childThreadId, prompt.trim());
    }
  }
  return promptByChildThreadId;
}

export type ChildTaskStatus = "running" | "finished" | "failed" | "stopped";

/**
 * Level-based task status of a sub-agent from its child thread's canonical
 * lifecycle. The session (running status + active turn) is the authoritative
 * "still working" signal — the projected turn state flips to `completed` on
 * every non-streaming interim assistant message mid-turn and only classifies
 * the terminal outcome once the session settles. Event-derived `taskStatus`
 * is a fallback for entries whose child thread state is unavailable
 * (archived or pre-migration histories).
 */
export function deriveChildTaskStatus(
  latestTurn: Pick<OrchestrationLatestTurn, "state"> | null | undefined,
  session: Pick<ThreadSession, "orchestrationStatus" | "activeTurnId"> | null | undefined,
): ChildTaskStatus | null {
  if (session?.orchestrationStatus === "running" && session.activeTurnId != null) {
    return "running";
  }
  switch (latestTurn?.state) {
    case "running":
      return "running";
    case "completed":
      return "finished";
    case "error":
      return "failed";
    case "interrupted":
      return "stopped";
    default:
      return null;
  }
}

export function derivePhase(session: ThreadSession | null): SessionPhase {
  if (!session || session.status === "closed") return "disconnected";
  if (session.status === "connecting") return "connecting";
  if (session.status === "running") return "running";
  return "ready";
}
